import { type CSSProperties, forwardRef } from 'react';

import { Link } from 'react-router-dom';
import type { ColorName } from 'styles/config/colors';

import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';
import { IconSize } from '../Icons/BaseIcon';
import './HoverTarget.css';

type KeyType = keyof typeof IconSize;

type PropsType = {
  $iconSize?: KeyType;
  $hoverSize?: KeyType;
  $hoverTargetColor?: ColorName;
};

export type HoverProps = {
  $hoverTarget?: boolean;
  $hoverIconColor?: ColorName;
  $circle?: boolean;
  $fullWidth?: boolean;
  $showFocusOutline?: boolean;
} & PropsType;

const getDefaultedSize = (defaultSize: KeyType) => (size?: KeyType) =>
  IconSize[size || defaultSize];
const getHoverSize = getDefaultedSize('xxl');
const getIconSize = getDefaultedSize('md');

export { CircleCss, HoverTargetCss } from './HoverTarget.styled';

function buildHoverStyle(props: HoverProps): CSSProperties {
  const style: CSSProperties & Record<string, string> = {};
  if (props.$hoverTarget && !props.$circle) {
    const hoverSize = getHoverSize(props.$hoverSize);
    const iconSize = getIconSize(props.$iconSize);
    const inset = -((hoverSize - iconSize) / 2);
    style['--hover-inset'] = `${inset}px`;
    style['--hover-bg'] = colorCssVar(
      (props.$hoverTargetColor || 'backgroundPage') as ColorName,
    );
  }
  if (props.$hoverIconColor) {
    style['--hover-icon-fill'] = colorCssVar(props.$hoverIconColor);
  }
  return style;
}

function buildHoverClass(props: HoverProps): string {
  return cn(
    props.$hoverTarget && !props.$circle && 'hover-target',
    props.$hoverIconColor && 'hover-icon-color',
    props.$circle &&
      'flex items-center justify-center w-md1 h-md1 !p-0 bg-background-page border border-solid border-border-strong rounded-full hover:bg-background-hover focus:bg-background-hover',
    props.$fullWidth && 'w-full',
    props.$showFocusOutline &&
      'focus-visible:outline-2 focus-visible:outline-[var(--tw-border-focus)] focus-visible:-outline-offset-2 focus-visible:not-disabled:outline',
  );
}

export const HoverButton = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & HoverProps
>(
  (
    {
      $hoverTarget,
      $hoverIconColor,
      $circle,
      $fullWidth,
      $showFocusOutline,
      $iconSize,
      $hoverSize,
      $hoverTargetColor,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const hoverProps: HoverProps = {
      $hoverTarget,
      $hoverIconColor,
      $circle,
      $fullWidth,
      $showFocusOutline,
      $iconSize,
      $hoverSize,
      $hoverTargetColor,
    };
    return (
      <button
        ref={ref}
        className={cn(buildHoverClass(hoverProps), className)}
        style={{ ...buildHoverStyle(hoverProps), ...style }}
        {...props}
      />
    );
  },
);
HoverButton.displayName = 'HoverButton';

export const HoverLink = forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithRef<typeof Link> & HoverProps
>(
  (
    {
      $hoverTarget,
      $hoverIconColor,
      $circle,
      $fullWidth,
      $showFocusOutline,
      $iconSize,
      $hoverSize,
      $hoverTargetColor,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const hoverProps: HoverProps = {
      $hoverTarget,
      $hoverIconColor,
      $circle,
      $fullWidth,
      $showFocusOutline,
      $iconSize,
      $hoverSize,
      $hoverTargetColor,
    };
    return (
      <Link
        ref={ref}
        className={cn(buildHoverClass(hoverProps), className)}
        style={{ ...buildHoverStyle(hoverProps), ...style }}
        {...props}
      />
    );
  },
);
HoverLink.displayName = 'HoverLink';

export const HoverAnchor = forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement> & HoverProps
>(
  (
    {
      $hoverTarget,
      $hoverIconColor,
      $circle,
      $fullWidth,
      $showFocusOutline,
      $iconSize,
      $hoverSize,
      $hoverTargetColor,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const hoverProps: HoverProps = {
      $hoverTarget,
      $hoverIconColor,
      $circle,
      $fullWidth,
      $showFocusOutline,
      $iconSize,
      $hoverSize,
      $hoverTargetColor,
    };
    return (
      <a
        ref={ref}
        className={cn(buildHoverClass(hoverProps), className)}
        style={{ ...buildHoverStyle(hoverProps), ...style }}
        {...props}
      />
    );
  },
);
HoverAnchor.displayName = 'HoverAnchor';
